import type { PropsWithChildren, ReactNode } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

const containerStyles = css({
    display: "flex",
    justifyContent: "flex-end",
    maxWidth: 1200,
    gap: 32,
    [media.below.md]: {
        width: "100%",
        flexDirection: "column-reverse",
        backgroundColor: "#FFF",
        gap: 0
    },
    [media.only.md]: {
        height: "80%"
    },
    [media.only.sm]: {
        height: "80%"
    },
    [media.only.xs]: {
        height: "80%"
    },
    [media.below.xs]: {
        height: "90%"
    }
});

const containerReverseStyles = css({
    flexDirection: "row-reverse",

    [media.below.md]: {
        flexDirection: "column"
    }
});

const contentStyles = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    marginInlineStart: "auto",
    marginInlineEnd: "auto"
});

const textContentStyles = css({
    flexBasis: "30%",
    borderRadius: 22,
    background: "#FFF",
    [media.below.md]: {
        width: "100%"
    },
    [media.above.sm]: {
        minWidth: 367
    }
});

const textSpaceStyles = css({
    paddingBlockStart: 12
});

const eyebrowStyles = css({
    marginBlockEnd: 5
});

const titleStyles = css({
    marginBlock: 0
});

const descriptionStyles = css({
    marginBlockStart: 16,
    [media.below.sm]: {
        marginBlockEnd: 16
    }
});

const separatorStyles = css({
    height: "1px",
    background: "#96999E",
    marginBlockStart: 32
});

const iconTitleStyles = css({
    marginBlockStart: 32,
    marginBlockEnd: 9,
    width: 241
});

const iconContainerStyles = css({
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 8,
    [media.below.sm]: {
        gap: 4
    }
});

const iconAndTextStyles = css({
    "display": "flex",
    "justifyContent": "center",
    "alignItems": "center",
    ".body-sm": {
        marginBlockStart: 0,
        marginBlockEnd: 0
    }
});

const iconStyles = css({
    width: 24,
    height: 24,
    textAlign: "center"
});

const buttonSpaceStyles = css({
    marginBlockStart: 32,
    [media.below.sm]: {
        marginBlockStart: 16,
        marginBlockEnd: 16
    }
});

const displayNoneBelowLg = css({
    [media.below.md]: {
        display: "none"
    }
});

export type FeatureInfoProps = PropsWithChildren<{
    cta?: ReactNode;
    eyebrow?: string;
    title: string;
    description: string;
    reverseLayout?: boolean;
    iconTitle?: string;
    iconCollection: { icon: ReactNode; description: string }[];
}>;

export const FeatureInfo = ({
    eyebrow,
    title,
    description,
    iconTitle,
    iconCollection,
    reverseLayout,
    cta,
    children
}: FeatureInfoProps) => {
    return (
        <div css={[containerStyles, reverseLayout && containerReverseStyles]}>
            <div css={contentStyles}>{children}</div>
            <div css={textContentStyles}>
                <div css={textSpaceStyles}>
                    {eyebrow && (
                        <div css={eyebrowStyles} className="eyebrow-md">
                            {eyebrow}
                        </div>
                    )}
                    <h3 css={titleStyles}>{title}</h3>
                    <p css={descriptionStyles}>{description}</p>
                    <div css={[separatorStyles, displayNoneBelowLg]} />
                    {iconTitle && (
                        <div css={[iconTitleStyles, displayNoneBelowLg]}>
                            {iconTitle}
                        </div>
                    )}
                    <div css={iconContainerStyles}>
                        {iconCollection.map((icon, index) => (
                            <div css={iconAndTextStyles} key={index}>
                                <div css={iconStyles}>{icon.icon}</div>
                                <p className="body-sm">{icon.description}</p>
                            </div>
                        ))}
                    </div>
                    {cta && <div css={buttonSpaceStyles}>{cta}</div>}
                </div>
            </div>
        </div>
    );
};
